import { USERNAME_MAX_LENGTH, USERNAME_PROBLEM_MESSAGES, usernameProblem } from '@mogzo/shared';
import { Check, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { checkUsername, type UsernameCheck } from '../lib/api';
import { inputClass } from './ui';

export type UsernameState = UsernameCheck['status'] | 'checking' | 'empty';

/** How long typing has to pause before the name is checked. */
const PAUSE_MS = 350;

/**
 * A username box that checks the name as the player types: free, taken (with free names like
 * it to tap), not allowed, or already theirs. The rules are checked here first, so only names
 * that could be used are sent to MOGZO.
 */
export function UsernameField({
  value,
  onChange,
  onState,
  label = 'Username',
  hint,
  disabled,
  focusOnShow,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Lets the form hold its button until the name is free. */
  onState?: (state: UsernameState) => void;
  label?: string;
  hint?: string;
  disabled?: boolean;
  /** Takes the keyboard when it appears, after the player asked to type their own. */
  focusOnShow?: boolean;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when the field first appears
  useEffect(() => {
    if (focusOnShow) input.current?.focus();
  }, []);
  const name = value.trim();
  const problem = name ? usernameProblem(name) : null;
  const [result, setResult] = useState<{ name: string; check: UsernameCheck } | null>(null);

  useEffect(() => {
    if (!name || problem || disabled) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      checkUsername(name, controller.signal)
        .then((check) => setResult({ name, check }))
        .catch(() => undefined);
    }, PAUSE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [name, problem, disabled]);

  const check = result?.name === name ? result.check : null;
  const state: UsernameState = !name
    ? 'empty'
    : problem
      ? 'invalid'
      : (check?.status ?? 'checking');
  useEffect(() => onState?.(state), [state, onState]);

  const message =
    state === 'invalid' && problem
      ? USERNAME_PROBLEM_MESSAGES[problem]
      : state === 'checking'
        ? 'Checking…'
        : check?.message;
  const tone =
    state === 'free'
      ? 'text-[#157244]'
      : state === 'taken' || state === 'invalid'
        ? 'text-red-700'
        : 'text-muted';
  const border =
    state === 'free'
      ? 'border-[#157244] bg-card'
      : state === 'taken' || state === 'invalid'
        ? 'border-red-600 bg-card'
        : '';

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-semibold text-ink">
        {label}
      </label>
      <input
        id={id}
        name="username"
        autoComplete="username"
        spellCheck={false}
        autoCapitalize="off"
        required
        maxLength={USERNAME_MAX_LENGTH}
        value={value}
        ref={input}
        disabled={disabled}
        aria-invalid={state === 'taken' || state === 'invalid' || undefined}
        aria-describedby={`${id}-check ${id}-hint`}
        onChange={(event) => onChange(event.target.value)}
        className={`${inputClass} font-semibold ${border}`}
      />
      <p
        id={`${id}-check`}
        role="status"
        aria-live="polite"
        className={`flex min-h-5 items-center gap-1.5 text-sm font-semibold ${tone}`}
      >
        {state === 'free' && <Check size={16} strokeWidth={2.6} aria-hidden="true" />}
        {(state === 'taken' || state === 'invalid') && (
          <X size={16} strokeWidth={2.6} aria-hidden="true" />
        )}
        {state !== 'empty' && message}
      </p>
      {state === 'taken' && check && check.suggestions.length > 0 && (
        <div>
          <p className="mb-2 text-[13px] text-muted">These are free:</p>
          <div className="flex flex-wrap gap-2">
            {check.suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => onChange(suggestion)}
                className="min-h-9 rounded-full border border-line bg-card px-3.5 text-[13px] font-bold text-ink hover:border-muted"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
      <p id={`${id}-hint`} className="text-xs text-muted">
        {hint ?? '3 to 20 letters, numbers or _. It’s what everyone sees: no real name needed.'}
      </p>
    </div>
  );
}
